import React, { createContext, useContext, useEffect, useState } from 'react'
import { useQuery, useSubscription, useMutation } from '@apollo/client'
import { GET_ME, LOGOUT } from '@/lib/graphql/auth'
import { USER_EVENTS_SUBSCRIPTION } from '@/lib/graphql/voice'
import { ToastGo } from '@/components/ui/toast-go'
import { getAuthToken, clearAuthTokens, apolloClient } from '@/lib/apollo-client'
import { initializeTokenRefresh, refreshAccessToken, cleanupTokenRefresh } from '@/lib/token-refresh'
import { toast } from '@/hooks/use-toast'
import { Button } from '@/components/ui/button'

interface User {
  id: string
  xUsername: string
  xDisplayName?: string
  xProfileImage?: string
  phoneNumber?: string
  phoneVerified: boolean
  subscription?: {
    status: string
    tier?: string
    currentPeriodEnd?: string
  }
  credits: number
  creatorScore?: number
  timezone?: string
  /** Public-demo visitor without an X account. */
  isGuest?: boolean
  guestMessagesLeft?: number
  /** The guest already got their first options card; the demo is over. */
  guestDemoComplete?: boolean
  createdAt: string
  lastLoginAt: string
}

interface AuthContextType {
  user: User | null
  loading: boolean
  error: any
  isAuthenticated: boolean
  isAuthenticating: boolean
  logout: () => void
  refetch: () => void
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  error: null,
  isAuthenticated: false,
  isAuthenticating: false,
  logout: () => {},
  refetch: () => {},
})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: React.ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [isInitialized, setIsInitialized] = useState(false)
  const [isAuthenticating, setIsAuthenticating] = useState(false)
  //const [refetchTrigger, setRefetchTrigger] = useState(0)

  // Always try to fetch user data - the httpOnly cookie will be sent automatically
  // This replaces the need to check localStorage
  const { data, loading, error, refetch } = useQuery(GET_ME, {
    skip: !isInitialized,
    errorPolicy: 'all',
    fetchPolicy: 'network-only', // Always check with server for fresh auth state
    notifyOnNetworkStatusChange: false,
    onError: (error) => {
      console.log('❌ Auth query error:', error)
      // If authentication fails, clear tokens
      if (error.networkError && 'statusCode' in error.networkError && error.networkError.statusCode === 401) {
        clearAuthTokens()
        setIsAuthenticating(false)
      }
    },
    onCompleted: (data) => {
      if (data?.me) {
        console.log('✅ User authenticated via cookie:', {
          id: data.me.id,
          username: data.me.xUsername,
          phoneVerified: data.me.phoneVerified
        })
        setIsAuthenticating(false)

        // Sync to localStorage for backward compatibility (extension, etc.)
        const token = getAuthToken()
        if (!token) {
          console.log('📝 User authenticated but no localStorage token - cookie-only auth')
        }
      }
    }
  })

  // Initialize on client side
  useEffect(() => {
    const initializeAuth = async () => {
      // Skip auto-refresh if we're on the OAuth callback page
      // The callback page will handle authentication itself
      const isOAuthCallback = window.location.pathname === '/auth/callback' ||
                              window.location.pathname === '/auth/x/callback'

      if (isOAuthCallback) {
        console.log('🔄 On OAuth callback page, skipping auto-refresh')
        setIsInitialized(true)
        return
      }

      // Check if we have a token in localStorage
      const token = getAuthToken()

      if (token) {
        // We have a token, initialize normal token refresh
        initializeTokenRefresh()
      } else {
        // No token in localStorage, but we might have a refresh token cookie
        // Try to refresh to get an access token
        console.log('🍪 No localStorage token, attempting cookie-based refresh...')
        try {
          const refreshed = await refreshAccessToken()
          if (refreshed) {
            console.log('✅ Successfully refreshed from cookie')
            // Initialize token refresh for the new token
            initializeTokenRefresh()
          }
        } catch (error) {
          console.log('❌ Cookie-based refresh failed:', error)
        }
      }

      setIsInitialized(true)
    }

    initializeAuth()

    // Listen for authentication in progress
    const handleAuthStart = () => {
      console.log('🔄 Authentication started')
      setIsAuthenticating(true)
    }

    const handleAuthComplete = () => {
      console.log('✅ Authentication completed')
      setIsAuthenticating(false)
      // Initialize token refresh for the new token
      const newToken = getAuthToken()
      if (newToken) {
        initializeTokenRefresh()
      }
      // Trigger a refetch to get user data with new cookies
      refetch()
    }

    // Listen for token expiration and trigger refresh
    const handleTokenExpired = async () => {
      console.log('⚠️ Token expired event received, attempting refresh...')
      const success = await refreshAccessToken()
      if (!success) {
        console.log('❌ Token refresh failed in AuthProvider')
        // The refresh function already cleared tokens, refetch to update state
        refetch()
      } else {
        // Refetch user data with new token
        refetch()
      }
    }

    // Listen for token changes (for backward compatibility with localStorage)
    const handleTokenChanged = () => {
      console.log('🔄 Token changed, refetching user data')
      refetch()
    }

    window.addEventListener('authStart', handleAuthStart)
    window.addEventListener('authComplete', handleAuthComplete)
    window.addEventListener('authTokenExpired', handleTokenExpired)
    window.addEventListener('authTokenChanged', handleTokenChanged)
    window.addEventListener('storage', handleTokenChanged)

    return () => {
      window.removeEventListener('authStart', handleAuthStart)
      window.removeEventListener('authComplete', handleAuthComplete)
      window.removeEventListener('authTokenExpired', handleTokenExpired)
      window.removeEventListener('authTokenChanged', handleTokenChanged)
      window.removeEventListener('storage', handleTokenChanged)
      cleanupTokenRefresh()
    }
  }, [refetch])

  // Subscribe to user events site-wide for authenticated users
  const user = data?.me || null
  const isAuthenticated = !!user && !error
  
  useSubscription(USER_EVENTS_SUBSCRIPTION, {
    skip: !isAuthenticated, // Only subscribe when authenticated
    onData: ({ data: subData }) => {
      console.log('📢 UserEvents subscription data:', subData)
      if (subData?.data?.userEvents) {
        const event = subData.data.userEvents
        console.log('📢 User event received (site-wide):', event)
        
        // Show toast notifications for different event types
        switch (event.type) {
          case 'POST_CREATED':
            // In the chat the options card is already on screen; the toast
            // only covered it. It is for voice calls and other pages.
            if (window.location.pathname.startsWith('/chat')) break
            toast({
              title: event.title || "Composing Post",
              description: event.description || event.content,
            })
            break
            
          case 'POST_PUBLISHED':
          case 'REPLY_POSTED':
            toast({
              title: event.title || "Post Published!",
              description: event.description || event.content,
              action: event.url ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => window.open(event.url, '_blank')}
                >
                  View on X
                </Button>
              ) : undefined
            })
            break
            
          case 'POST_SCHEDULED':
            toast({
              title: "Post Scheduled",
              description: event.description || "Your post has been scheduled",
              action: window.location.pathname.startsWith('/queue') ? undefined : <ToastGo label="Open Slop Cannon" to="/queue" />,
            })
            break
            
          case 'DRAFT_SAVED':
            toast({
              title: "Draft Saved",
              description: event.description || "Your post has been saved as a draft",
              action: window.location.pathname.startsWith('/drafts') ? undefined : <ToastGo label="Open drafts" to="/drafts" />,
            })
            break
            
          case 'STYLE_ANALYZED':
            toast({
              title: "Style Analysis",
              description: event.description || "AI is analyzing your writing style",
            })
            break
            
          case 'THREAD_CREATED':
            toast({
              title: "Thread Created",
              description: event.description || "Your thread has been created",
            })
            break
            
          case 'POST_DELETED':
            toast({
              title: "Post Deleted",
              description: event.description || "Post has been deleted",
            })
            break
            
          case 'USER_FOLLOWED':
            toast({
              title: "User Followed",
              description: event.description || "Successfully followed user",
            })
            break
            
          case 'USER_UNFOLLOWED':
            toast({
              title: "User Unfollowed",
              description: event.description || "Successfully unfollowed user",
            })
            break
            
          case 'POST_LIKED':
            toast({
              title: "Post Liked",
              description: event.description || "Post liked successfully",
            })
            break
            
          case 'POST_UNLIKED':
            toast({
              title: "Post Unliked",
              description: event.description || "Post unliked",
            })
            break
            
          case 'POST_RETWEETED':
            toast({
              title: "Retweeted",
              description: event.description || "Successfully retweeted",
            })
            break
        }
      }
    },
    onError: (error) => {
      console.error('❌ User event subscription error (site-wide):', error)
    }
  })

  const [logoutMutation] = useMutation(LOGOUT, {
    context: {
      credentials: 'include'
    },
    onCompleted: () => {
      console.log('✅ Logout mutation completed, clearing local tokens...')
      // Clear localStorage tokens
      clearAuthTokens()

      // Reset Apollo cache to clear all cached user data
      apolloClient.clearStore()

      console.log('✅ Logged out successfully')
    },
    onError: (error) => {
      console.error('❌ Logout mutation failed:', error)
      // Still clear local tokens even if backend fails
      clearAuthTokens()

      // Reset Apollo cache
      apolloClient.clearStore()
    }
  })

  const logout = () => {
    console.log('🚪 Logout initiated, calling mutation...')
    setIsAuthenticating(false)
    // Mutation callbacks will handle the rest
    logoutMutation()
  }

  const isLoading = loading || !isInitialized || isAuthenticating

  const value: AuthContextType = {
    user,
    loading: isLoading,
    error,
    isAuthenticated,
    isAuthenticating,
    logout,
    refetch,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
